import { useEffect, useMemo, useState } from "react";
import { GitMerge, Scissors, Search, Sparkles } from "lucide-react";
import {
  CohortHeatmap,
  ThemeHierarchyChart,
  ThemeTrends,
} from "../analytical-charts";
import { PageHeader } from "../components";
import { emergingLanguage, themeHierarchy } from "../fixtures";
import { VoiceCloudChart } from "../charts";
import { useAnalyticalContext } from "../analytical-context";

const productTheme = {
  "Current Account": "Payments & billing",
  "Mobile App": "Account access",
  Delivery: "Orders & delivery",
  Insurance: "Renewal & retention",
} as const;

export function VoicePage() {
  const { filters } = useAnalyticalContext();
  const scopedProductTheme =
    filters.product === "All" ? undefined : productTheme[filters.product];
  const [selected, setSelected] = useState("Account access");
  const [query, setQuery] = useState("");
  const [audit, setAudit] = useState<string[]>([]);
  const scopedThemes = useMemo(
    () =>
      filters.product === "All"
        ? themeHierarchy
        : themeHierarchy.filter((theme) => theme.name === scopedProductTheme),
    [filters.product, scopedProductTheme],
  );
  useEffect(() => {
    if (scopedProductTheme) setSelected(scopedProductTheme);
  }, [scopedProductTheme]);
  const filtered = useMemo(
    () =>
      scopedThemes.filter((t) =>
        `${t.name} ${t.children.flatMap((c) => [c.name, ...c.children]).join(" ")}`
          .toLowerCase()
          .includes(query.toLowerCase()),
      ),
    [query, scopedThemes],
  );
  const theme =
    themeHierarchy.find((t) => t.name === selected) ?? themeHierarchy[1]!;
  const scopedLanguage =
    filters.product === "All"
      ? emergingLanguage
      : emergingLanguage.filter((item) =>
          item.proposal.toLowerCase().includes(theme.name.toLowerCase()),
        );
  const record = (action: string) =>
    setAudit((v) => [
      `${action} · local demo audit · ${new Date().toLocaleTimeString()}`,
      ...v,
    ]);
  return (
    <>
      <PageHeader
        eyebrow="Customer Voice · What needs and problems are customers expressing?"
        title={
          filters.product === "All"
            ? "Customers describe access failures, not forgotten passwords."
            : `${theme.children[0].name} is the dominant ${filters.product} customer need.`
        }
        description="Managed themes retain longitudinal stability while novel language identifies needs the current taxonomy may miss."
      />
      <section className="voice-hero">
        <div>
          <span className="kicker">Most material shift</span>
          <h2>
            {theme.name} is {theme.change > 0 ? "growing" : "declining"}{" "}
            {Math.abs(theme.change)}%.
          </h2>
          <p>
            {theme.children[0].name} now leads the hierarchy. Effort is{" "}
            {theme.effort}/10 and only {theme.outcome}% reach the intended
            outcome.
          </p>
        </div>
        <div>
          <strong>{theme.volume.toLocaleString()}</strong>
          <span>interactions</span>
          <small>
            {theme.confidence}% confidence · {theme.persistence}% persistent
          </small>
        </div>
      </section>
      <div className="coi-grid coi-grid--wide">
        <section className="panel analytics-panel">
          <div className="analytics-panel__heading">
            <div>
              <span className="kicker">Managed hierarchy</span>
              <h3>From broad theme to specific customer need</h3>
            </div>
            <span className="chart-unit">
              Select a node to update interpretation
            </span>
          </div>
          <ThemeHierarchyChart selected={selected} onSelect={setSelected} />
        </section>
        <section className="panel theme-detail">
          <span className="kicker">Selected theme</span>
          <h3>{theme.name}</h3>
          <dl>
            <div>
              <dt>Share</dt>
              <dd>{theme.share}%</dd>
            </div>
            <div>
              <dt>Novelty</dt>
              <dd>{theme.novelty}%</dd>
            </div>
            <div>
              <dt>Persistence</dt>
              <dd>{theme.persistence}%</dd>
            </div>
            <div>
              <dt>Effort</dt>
              <dd>{theme.effort}/10</dd>
            </div>
            <div>
              <dt>Outcome</dt>
              <dd>{theme.outcome}%</dd>
            </div>
            <div>
              <dt>Confidence</dt>
              <dd>{theme.confidence}%</dd>
            </div>
          </dl>
          <div className="local-actions">
            <button
              type="button"
              onClick={() => record(`Merged ${theme.name}`)}
            >
              <GitMerge size={14} /> Merge
            </button>
            <button type="button" onClick={() => record(`Split ${theme.name}`)}>
              <Scissors size={14} /> Split
            </button>
            <button
              type="button"
              onClick={() => record(`Promoted ${theme.name}`)}
            >
              <Sparkles size={14} /> Promote
            </button>
          </div>
        </section>
      </div>
      {filters.product === "All" && (
        <div className="coi-grid">
          <section className="panel analytics-panel voice-cloud-panel">
            <div className="analytics-panel__heading">
              <div>
                <span className="kicker">Language discovery</span>
                <h3>The words customers use, grouped by intent</h3>
              </div>
              <span className="chart-unit">Size = interaction volume</span>
            </div>
            <VoiceCloudChart />
            <p className="chart-insight">
              <span />
              Payment dates and identity checks dominate customer language; the
              managed hierarchy above remains the primary analytical view.
            </p>
          </section>
          <section className="panel analytics-panel">
            <h3>How are leading themes evolving?</h3>
            <ThemeTrends />
            <p className="chart-insight">
              <span />
              Account access accelerated after 1 July; delivery needs declined
              after revised tracking language.
            </p>
          </section>
        </div>
      )}
      <section className="panel emerging-language">
        <div className="analytics-panel__heading">
          <div>
            <span className="kicker">Uncategorised language</span>
            <h3>New phrases that may require taxonomy change</h3>
          </div>
        </div>
        {scopedLanguage.map((item) => (
          <article key={item.phrase}>
            <div>
              <strong>“{item.phrase}”</strong>
              <span>
                {item.count} interactions · +{item.acceleration}% acceleration ·
                first seen {item.firstSeen}
              </span>
            </div>
            <p>Proposed: {item.proposal}</p>
            <button
              type="button"
              onClick={() => record(`Promoted phrase: ${item.phrase}`)}
            >
              Promote
            </button>
          </article>
        ))}
      </section>
      {filters.product === "All" && (
        <section className="panel analytics-panel voice-cohort-panel">
          <h3>Which cohorts over-index on each need?</h3>
          <CohortHeatmap title="Deviation from each cohort's expected theme share" />
        </section>
      )}
      <section className="panel theme-panel">
        <div className="filter-row">
          <div className="inline-search">
            <Search size={16} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search themes and specific needs"
            />
          </div>
          <span>{filtered.length} matching top-level themes</span>
        </div>
        <div className="theme-header">
          <span>Theme hierarchy</span>
          <span>Volume</span>
          <span>Share</span>
          <span>Change</span>
        </div>
        {filtered.map((item) => (
          <button
            className="theme-row"
            type="button"
            key={item.id}
            onClick={() => setSelected(item.name)}
          >
            <span className="theme-title">
              <strong>{item.name}</strong>
              <small>{item.children.map((c) => c.name).join(" · ")}</small>
            </span>
            <strong>{item.volume.toLocaleString()}</strong>
            <span>{item.share}%</span>
            <span>
              {item.change > 0 ? "+" : ""}
              {item.change}%
            </span>
          </button>
        ))}
      </section>
      {audit.length > 0 && (
        <section className="audit-strip">
          <strong>Local governance history</strong>
          {audit.slice(0, 3).map((entry) => (
            <span key={entry}>{entry}</span>
          ))}
        </section>
      )}
    </>
  );
}
